import { useState, useEffect, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { supabase } from "./supabase";

function useInstallBanner() {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    const isStandalone = window.navigator.standalone === true || window.matchMedia("(display-mode: standalone)").matches;
    const wasDismissed = localStorage.getItem("pwa-banner-dismissed") === "true";
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isSafari = /safari/i.test(navigator.userAgent) && !/crios|fxios|opios|chrome/i.test(navigator.userAgent);
    if (!isStandalone && !wasDismissed && isIOS && isSafari) setTimeout(() => setShow(true), 1500);
  }, []);
  function dismiss() { setDismissed(true); setShow(false); localStorage.setItem("pwa-banner-dismissed", "true"); }
  return { show: show && !dismissed, dismiss };
}

function InstallBanner({ onDismiss }) {
  return (
    <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 999, background: "var(--darker)", borderTop: "1.5px solid var(--gold)", padding: "18px 20px 28px", animation: "slideUp 0.4s cubic-bezier(0.16,1,0.3,1)", boxShadow: "0 -8px 32px rgba(0,0,0,0.5)" }}>
      <style>{`@keyframes slideUp{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
      <button onClick={onDismiss} style={{ position: "absolute", top: 12, right: 16, background: "none", border: "none", color: "var(--muted)", fontSize: 20, cursor: "pointer" }}>×</button>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontFamily: "sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text)", marginBottom: 3 }}>✂ Installer l'app Mendes Cut</div>
        <div style={{ fontSize: 11, color: "var(--muted)" }}>Accès rapide depuis ton écran d'accueil</div>
      </div>
      {[{ icon: "⬆", text: "Appuie sur le bouton Partager en bas de Safari" }, { icon: "＋", text: 'Sélectionne "Sur l\'écran d\'accueil"' }, { icon: "✓", text: "Appuie sur Ajouter — c'est fait !" }].map((s, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", background: "rgba(200,169,110,0.15)", border: "1px solid rgba(200,169,110,0.4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: "var(--gold)" }}>{s.icon}</div>
          <div style={{ fontSize: 12, color: "var(--body)" }}>{s.text}</div>
        </div>
      ))}
    </div>
  );
}

const SERVICES = [
  { id: 1, category: "COUPE", name: "Coupe Simple", desc: "Dégradé ou taper", price: 25, duration: "30 min" },
  { id: 2, category: "COUPE", name: "MendesTransfo'", desc: "Coupe spéciale signature", price: 40, duration: "50 min", featured: true },
  { id: 3, category: "PRESTATION", name: "Contours", desc: "Contours cheveux uniquement", price: 15, duration: "20 min" },
  { id: 4, category: "PRESTATION", name: "Barbe Complète", desc: "Taille & soin complet de barbe", price: 15, duration: "20 min" },
];

const SUPPLEMENTS = [
  { id: "s1", name: "Longueur ciseaux / afro", price: 5 },
  { id: "s2", name: "Design", price: 5 },
  { id: "s3", name: "Bouc et moustache", price: 5 },
  { id: "s4", name: "Traçages de barbe", price: 5 },
  { id: "s5", name: "Barbe complète (+coupe)", price: 10 },
];

const DAYS_SHORT = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const DAYS_FULL = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
const SLOTS_AM = ["09:00", "09:45", "10:30", "11:15", "12:00", "12:45", "13:30"];
const SLOTS_PM = ["14:15", "15:00", "15:45", "16:30", "17:15", "18:00"];
const MONTHS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

// Les couleurs passent par les variables CSS définies dans le <style> ci-dessous
// (palette sombre par défaut, claire via :root[data-theme="light"] posé par App.js)
const GOLD = "var(--gold)";
const DARK = "var(--dark)";
const DARKER = "var(--darker)";
const CARD = "var(--card)";
const BORDER = "var(--border)";
const MUTED = "var(--muted)";

function getDaysForMonth(year, month) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    if (date.getDay() === 0) continue;
    if (date < today) continue;
    days.push({ date: d, month: month + 1, dayIdx: date.getDay() === 0 ? 6 : date.getDay() - 1, full: date });
  }
  return days;
}

function toISODate(year, month, date) {
  return `${year}-${String(month).padStart(2, "0")}-${String(date).padStart(2, "0")}`;
}

// Date + heure actuelles en Suisse (Europe/Zurich), indépendantes du fuseau de l'appareil
function zurichNow() {
  const parts = new Intl.DateTimeFormat("fr-CH", {
    timeZone: "Europe/Zurich", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value || "00";
  return { dateISO: `${get("year")}-${get("month")}-${get("day")}`, heure: `${get("hour")}:${get("minute")}` };
}

function isSlotPast(dateISO, heure) {
  const now = zurichNow();
  return dateISO < now.dateISO || (dateISO === now.dateISO && heure <= now.heure);
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Lato:wght@300;400;700&display=swap');
*{box-sizing:border-box;margin:0;padding:0;}
:root{--gold:#C8A96E;--dark:#2B2925;--darker:#201E1A;--card:#332F29;--border:rgba(200,169,110,0.2);--muted:#7A7060;--text:#F0EAE0;--body:#D0CAC0;--gold-hover:#E2C88A;}
:root[data-theme="light"]{--gold:#A8894E;--dark:#FFFFFF;--darker:#F4EFE7;--card:#F0EAE0;--border:rgba(140,110,60,0.3);--muted:#8A8070;--text:#2B2925;--body:#5A5348;--gold-hover:#8F7440;}
html,body{background:var(--darker);color:var(--text);font-family:'Lato',sans-serif;}
.app{max-width:430px;margin:0 auto;min-height:100vh;background:var(--dark);display:flex;flex-direction:column;}
.hdr{background:var(--darker);padding-top:max(20px,env(safe-area-inset-top));padding-right:20px;padding-bottom:14px;padding-left:20px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;}
.hdr-title{font-family:'Bebas Neue',sans-serif;font-size:26px;letter-spacing:0.08em;line-height:1;color:var(--text);}
.hdr-title span{color:var(--gold);}
.hdr-sub{font-size:9px;letter-spacing:0.35em;color:var(--muted);text-transform:uppercase;margin-top:3px;}
.nav{display:flex;background:var(--darker);border-bottom:1px solid var(--border);position:sticky;top:0;z-index:50;}
.nb{flex:1;padding:10px 2px 9px;background:none;border:none;color:var(--muted);font-family:'Lato',sans-serif;font-size:8.5px;letter-spacing:0.18em;text-transform:uppercase;cursor:pointer;display:flex;flex-direction:column;align-items:center;gap:4px;transition:color .2s;position:relative;}
.nb.on{color:var(--gold);}
.nb.on::after{content:'';position:absolute;bottom:0;left:25%;right:25%;height:1.5px;background:var(--gold);}
.pg{flex:1;overflow-y:auto;padding:18px 16px 32px;animation:fu .25s ease;}
@keyframes fu{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
.steps{display:flex;gap:5px;margin-bottom:18px;}
.step-bar{flex:1;height:2px;border-radius:1px;transition:background .3s;}
.sh{font-family:'Bebas Neue',sans-serif;font-size:24px;letter-spacing:0.06em;margin-bottom:2px;}
.sh span{color:var(--gold);}
.ss{font-size:9px;letter-spacing:0.25em;color:var(--muted);text-transform:uppercase;margin-bottom:18px;}
.svc-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:16px;}
.svc{background:var(--card);border:1px solid var(--border);border-radius:3px;padding:14px 12px;cursor:pointer;transition:all .2s;position:relative;}
.svc:hover{border-color:rgba(200,169,110,.45);}
.svc.sel{border-color:var(--gold);background:rgba(200,169,110,.1);}
.svc.sel::after{content:'✓';position:absolute;top:8px;right:10px;font-size:10px;color:var(--gold);}
.svc.feat{border-color:rgba(200,169,110,.35);}
.svc-cat{font-size:8px;letter-spacing:.25em;color:var(--muted);text-transform:uppercase;margin-bottom:6px;}
.svc-name{font-family:'Bebas Neue',sans-serif;font-size:17px;letter-spacing:.05em;margin-bottom:4px;line-height:1;}
.svc-desc{font-size:10px;color:var(--muted);margin-bottom:10px;line-height:1.4;}
.svc-foot{display:flex;justify-content:space-between;align-items:center;}
.svc-price{font-size:14px;color:var(--gold);font-weight:700;}
.svc-dur{font-size:9px;color:var(--muted);}
.featured-badge{position:absolute;top:-1px;right:-1px;background:var(--gold);color:var(--darker);font-size:7px;letter-spacing:.15em;padding:3px 7px;border-radius:0 3px 0 3px;font-weight:700;text-transform:uppercase;}
.supp-title{font-size:9px;letter-spacing:.2em;color:var(--muted);text-transform:uppercase;margin-bottom:10px;margin-top:4px;}
.supp-list{display:flex;flex-direction:column;gap:6px;margin-bottom:18px;}
.supp-item{display:flex;align-items:center;gap:10px;background:var(--card);border:1px solid var(--border);border-radius:3px;padding:10px 12px;cursor:pointer;transition:all .2s;}
.supp-item.sel{border-color:var(--gold);background:rgba(200,169,110,.08);}
.supp-check{width:16px;height:16px;border:1px solid var(--border);border-radius:2px;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:10px;}
.supp-item.sel .supp-check{background:var(--gold);border-color:var(--gold);color:var(--darker);}
.supp-name{flex:1;font-size:12px;color:var(--body);}
.supp-price{font-size:11px;color:var(--gold);}
.slot-period{font-size:9px;letter-spacing:.2em;color:var(--muted);text-transform:uppercase;margin-bottom:8px;}
.slot-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:7px;margin-bottom:14px;}
.slot{padding:9px 4px;background:var(--card);border:1px solid var(--border);border-radius:3px;text-align:center;font-size:11px;cursor:pointer;transition:all .15s;color:var(--body);}
.slot:hover:not(.off):not(.taken){border-color:rgba(200,169,110,.4);}
.slot.sel{border-color:var(--gold);background:rgba(200,169,110,.12);color:var(--gold);}
.slot.off{opacity:.28;cursor:not-allowed;text-decoration:line-through;color:var(--muted);}
.slot.taken{opacity:.5;cursor:not-allowed;text-decoration:line-through;color:#d08080;border-color:rgba(200,80,80,.25);background:rgba(200,80,80,.06);}
.slot-legend{display:flex;gap:10px;margin-bottom:12px;flex-wrap:wrap;}
.legend-item{display:flex;align-items:center;gap:5px;font-size:9px;color:var(--muted);}
.legend-dot{width:9px;height:9px;border-radius:2px;flex-shrink:0;}
.field{margin-bottom:14px;}
.field label{display:block;font-size:9px;letter-spacing:.22em;text-transform:uppercase;color:var(--muted);margin-bottom:7px;}
.field input{width:100%;background:var(--card);border:1px solid var(--border);border-radius:3px;padding:12px 13px;color:var(--text);font-family:'Lato',sans-serif;font-size:16px;outline:none;transition:border-color .2s;}
.field input:focus{border-color:var(--gold);}
.field input::placeholder{color:var(--muted);}
.sum{background:var(--card);border:1px solid var(--border);border-radius:3px;padding:14px;margin-bottom:16px;}
.sum-row{display:flex;justify-content:space-between;align-items:flex-start;padding:7px 0;border-bottom:1px solid var(--border);font-size:11px;gap:8px;}
.sum-row:last-child{border-bottom:none;padding-bottom:0;}
.sum-label{color:var(--muted);}
.sum-val{color:var(--text);text-align:right;}
.sum-val.gold{color:var(--gold);font-family:'Bebas Neue',sans-serif;font-size:16px;}
.sum-total{font-size:18px !important;}
.notice{background:rgba(200,169,110,.08);border:1px solid rgba(200,169,110,.25);border-radius:3px;padding:12px 14px;display:flex;gap:11px;align-items:flex-start;margin-bottom:14px;}
.notice-icon{font-size:16px;flex-shrink:0;}
.notice-text{font-size:10px;color:var(--muted);line-height:1.6;}
.notice-text strong{color:var(--body);display:block;margin-bottom:2px;font-size:11px;font-weight:700;}
.btn{width:100%;padding:14px;background:var(--gold);border:none;border-radius:3px;color:var(--darker);font-family:'Bebas Neue',sans-serif;font-size:15px;letter-spacing:.2em;cursor:pointer;transition:all .2s;}
.btn:hover{background:var(--gold-hover);}
.btn:disabled{opacity:.35;cursor:not-allowed;}
.btn-ghost{width:100%;padding:12px;background:transparent;border:1px solid var(--border);border-radius:3px;color:var(--muted);font-family:'Lato',sans-serif;font-size:10px;letter-spacing:.18em;text-transform:uppercase;cursor:pointer;transition:all .2s;margin-top:9px;}
.btn-ghost:hover{border-color:var(--gold);color:var(--gold);}
.confirm-center{text-align:center;padding:28px 0 20px;}
.confirm-ring{width:62px;height:62px;border:1.5px solid var(--gold);border-radius:50%;display:flex;align-items:center;justify-content:center;margin:0 auto 14px;font-size:22px;animation:pop .5s cubic-bezier(.175,.885,.32,1.275);}
@keyframes pop{0%{transform:scale(0);opacity:0}100%{transform:scale(1);opacity:1}}
.confirm-h{font-family:'Bebas Neue',sans-serif;font-size:28px;letter-spacing:.05em;margin-bottom:4px;}
.confirm-h span{color:var(--gold);}
.confirm-s{font-size:10px;color:var(--muted);letter-spacing:.15em;}
.social-card{display:flex;align-items:center;gap:13px;background:var(--card);border:1px solid var(--border);border-radius:3px;padding:14px;margin-bottom:9px;cursor:pointer;transition:all .2s;text-decoration:none;}
.social-card:hover{border-color:rgba(200,169,110,.4);transform:translateX(3px);}
.social-icon{width:42px;height:42px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.social-info{flex:1;}
.social-name{font-family:'Bebas Neue',sans-serif;font-size:18px;letter-spacing:.04em;margin-bottom:1px;color:var(--text);}
.social-handle{font-size:10px;color:var(--muted);}
.social-stat{font-size:10px;color:var(--gold);}
.policy{background:rgba(200,169,110,.06);border:1px solid rgba(200,169,110,.2);border-radius:3px;padding:14px;margin-bottom:10px;}
.policy-title{font-family:'Bebas Neue',sans-serif;font-size:15px;letter-spacing:.08em;color:var(--gold);margin-bottom:8px;display:flex;align-items:center;gap:6px;}
.policy-text{font-size:11px;color:var(--muted);line-height:1.7;}
.policy-text strong{color:var(--body);}
.pay-methods{display:flex;gap:8px;margin-top:10px;}
.pay-pill{padding:5px 10px;border-radius:20px;font-size:10px;}
.pay-ok{background:rgba(50,180,100,.1);border:1px solid rgba(50,180,100,.3);color:#7dca9a;}
.pay-no{background:rgba(200,80,80,.08);border:1px solid rgba(200,80,80,.2);color:#d08080;text-decoration:line-through;}
.map-box{background:var(--card);border:1px solid var(--border);border-radius:3px;height:180px;position:relative;overflow:hidden;margin-bottom:14px;}
.map-grid{position:absolute;inset:0;background-image:linear-gradient(rgba(200,169,110,.07) 1px,transparent 1px),linear-gradient(90deg,rgba(200,169,110,.07) 1px,transparent 1px);background-size:28px 28px;}
.map-road-h{position:absolute;left:0;right:0;height:10px;background:rgba(200,169,110,.1);}
.map-road-v{position:absolute;top:0;bottom:0;width:8px;background:rgba(200,169,110,.1);}
.map-pin{position:absolute;top:50%;left:50%;transform:translate(-50%,-60%);text-align:center;}
.map-pin-dot{width:14px;height:14px;background:var(--gold);border-radius:50%;margin:0 auto 4px;box-shadow:0 0 0 4px rgba(200,169,110,.25);}
.map-label{font-size:9px;color:var(--gold);letter-spacing:.15em;white-space:nowrap;background:rgba(43,41,37,.85);padding:3px 8px;border-radius:2px;}
.addr-card{background:var(--card);border:1px solid var(--border);border-radius:3px;padding:14px;margin-bottom:10px;}
.addr-row{display:flex;align-items:flex-start;gap:11px;margin-bottom:11px;}
.addr-row:last-child{margin-bottom:0;}
.addr-icon{font-size:14px;flex-shrink:0;margin-top:1px;}
.addr-label{font-size:8.5px;letter-spacing:.18em;color:var(--muted);text-transform:uppercase;margin-bottom:2px;}
.addr-val{font-size:12px;color:var(--body);line-height:1.5;}
.addr-val a{color:var(--gold);text-decoration:none;}
.divider{height:1px;background:var(--border);margin:18px 0;}
.slogan{text-align:center;font-family:'Bebas Neue',sans-serif;font-size:13px;letter-spacing:.3em;color:var(--muted);padding:8px 0;}
.day-closed{font-size:7px;color:#d08080;margin-top:2px;}
.refresh-indicator{font-size:9px;color:var(--muted);text-align:center;margin-bottom:8px;letter-spacing:0.1em;}
`;

export default function MendesCut() {
  const { show: showBanner, dismiss: dismissBanner } = useInstallBanner();
  const [tab, setTab] = useState("book");
  const [step, setStep] = useState(1);
  const [svcId, setSvcId] = useState(null);
  const [suppIds, setSuppIds] = useState([]);
  const [dayIdx, setDayIdx] = useState(null);
  const [slot, setSlot] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+41");
  const [booked, setBooked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saveErrorMsg, setSaveErrorMsg] = useState("");
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [reservedSlots, setReservedSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const todayDate = new Date();
  const [calMonth, setCalMonth] = useState(todayDate.getMonth());
  const [calYear, setCalYear] = useState(todayDate.getFullYear());

  // Pré-remplissage depuis le profil : nom, téléphone, et service ("Re-réserver")
  useEffect(() => {
    const savedName = localStorage.getItem("client-name");
    if (savedName) setName(savedName);
    const savedPhone = localStorage.getItem("client-phone");
    if (savedPhone) {
      const m = savedPhone.match(/^(\+\d{2})(\d+)$/);
      if (m) { setCountryCode(m[1]); setPhone(m[2]); }
    }
    const pre = localStorage.getItem("preselect-service");
    if (pre) {
      localStorage.removeItem("preselect-service");
      const s = SERVICES.find((x) => x.name === pre);
      if (s) setSvcId(s.id);
    }
  }, []);
  const days = getDaysForMonth(calYear, calMonth);

  // ✅ Charge les créneaux bloqués ET réservés — appelé à chaque passage à l'étape 2
  const fetchSlots = useCallback(async () => {
    setLoadingSlots(true);
    // Filtre sur les dates à venir : PostgREST plafonne à 1000 lignes par requête,
    // et blocked_slots accumule l'historique — sans filtre, les blocages récents
    // sortent de la fenêtre et deviennent invisibles.
    const todayISO = zurichNow().dateISO;
    const [{ data: blocked }, { data: reserved }] = await Promise.all([
      supabase.from("blocked_slots").select("*").or(`date.gte.${todayISO},date_end.gte.${todayISO}`),
      supabase.from("creneaux_occupes").select("*"), // vue sans données perso
    ]);
    setBlockedSlots(blocked || []);
    setReservedSlots(reserved || []);
    setLoadingSlots(false);
  }, []);

  // Chargement initial
  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  // ✅ Rechargement à chaque fois que l'utilisateur arrive sur l'étape 2
  useEffect(() => {
    if (step === 2) {
      fetchSlots();
      // ✅ Rafraîchissement automatique toutes les 30 secondes sur l'étape 2
      const interval = setInterval(fetchSlots, 10000);
      return () => clearInterval(interval);
    }
  }, [step, fetchSlots]);

  function isSlotBlocked(dateISO, heure) {
    return blockedSlots.some(b => {
      if (b.type === "slot") return b.date === dateISO && b.heure === heure;
      if (b.type === "day") return b.date === dateISO;
      if (b.type === "range") return dateISO >= b.date && dateISO <= b.date_end;
      return false;
    });
  }

  function isDayBlocked(dateISO) {
    return blockedSlots.some(b => {
      if (b.type === "day") return b.date === dateISO;
      if (b.type === "range") return dateISO >= b.date && dateISO <= b.date_end;
      return false;
    });
  }

  function isSlotTaken(dateISO, heure) {
    return reservedSlots.some(r => {
      if (!r.date || !r.heure) return false;
      // La date peut arriver en ISO ("2026-07-31") ou en texte français ("Jeudi 31.07.2026")
      const raw = String(r.date);
      let rISO = null;
      const iso = raw.match(/(\d{4})-(\d{2})-(\d{2})/);
      const fr = raw.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
      if (iso) rISO = `${iso[1]}-${iso[2]}-${iso[3]}`;
      else if (fr) rISO = `${fr[3]}-${String(fr[2]).padStart(2, "0")}-${String(fr[1]).padStart(2, "0")}`;
      if (!rISO) return false;
      // Un type "time" Postgres arrive en "09:00:00" — on compare sur HH:MM
      const rHeure = String(r.heure).slice(0, 5);
      return rISO === dateISO && rHeure === heure;
    });
  }

  function prevMonth() {
    const today = new Date();
    if (calYear === today.getFullYear() && calMonth === today.getMonth()) return;
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1);
    setDayIdx(null); setSlot(null);
  }

  function nextMonth() {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1);
    setDayIdx(null); setSlot(null);
  }

  const svc = SERVICES.find(s => s.id === svcId);
  const supps = SUPPLEMENTS.filter(s => suppIds.includes(s.id));
  const total = (svc?.price || 0) + supps.reduce((a, s) => a + s.price, 0);
  function toggleSupp(id) { setSuppIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); }

  async function saveReservation() {
    setSaving(true); setSaveError(false); setSaveErrorMsg("");

    // ✅ Vérification finale avant insertion : le créneau est-il encore libre ?
    await fetchSlots();
    const d = days[dayIdx];
    const dateISO = toISODate(d.full.getFullYear(), d.month, d.date);
    if (isSlotPast(dateISO, slot) || isSlotTaken(dateISO, slot) || isSlotBlocked(dateISO, slot)) {
      setSaveError(true);
      setSaving(false);
      setSlot(null);
      setStep(2);
      return;
    }

    const dateStr = `${DAYS_FULL[d.dayIdx]} ${d.date}.${String(d.month).padStart(2, "0")}.${d.full.getFullYear()}`;
    const fullPhone = countryCode + phone.replace(/^0/, "").replace(/[\s\-\.]/g, "");

    // RPC security definer : la table reservations n'est plus lisible/écrivable en direct
    const { data, error } = await supabase.rpc("creer_reservation", {
      p_nom: name, p_telephone: fullPhone, p_service: svc?.name,
      p_supplements: supps.map(s => s.name).join(", ") || null,
      p_date: dateStr, p_heure: slot, p_total: total,
    });

    // ⚠ C'est CE branchement qui affiche "Erreur de connexion" — l'insert Supabase, pas le push.
    if (error) {
      console.error("Insert réservation échouée:", error);
      // Affiche la vraie cause dans l'UI (code Postgres + message) pour debugger sur l'appareil.
      const friendly = error.code === "23505"
        ? "Ce créneau vient d'être réservé. Choisis-en un autre."
        : `${error.message || "Erreur inconnue"}${error.code ? ` [${error.code}]` : ""}`;
      setSaveErrorMsg(friendly);
      setSaveError(true); setSaving(false); return;
    }

    try {
      await fetch("https://mendescut-barbershop.com/api/send-sms", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: fullPhone, clientName: name, service: svc?.name, date: dateStr, heure: slot, total, cancelId: data?.id, platform: Capacitor.getPlatform() }),
      });
    } catch (e) { console.warn("SMS non envoyé:", e); }

    localStorage.setItem("client-phone", fullPhone); // pré-remplit "Mes RDV" et le profil
    localStorage.setItem("client-name", name);
    setReservedSlots(prev => [...prev, { date: dateStr, heure: slot }]);
    setSaving(false); setStep(4); setBooked(true);
  }

  function reset() {
    setStep(1); setSvcId(null); setSuppIds([]); setDayIdx(null);
    setSlot(null); setName(""); setPhone(""); setBooked(false); setSaveError(false);
    const t = new Date(); setCalMonth(t.getMonth()); setCalYear(t.getFullYear());
  }

  return (
    <>
      <style>{css}</style>
      <div className="app">
        <div className="hdr">
          <div>
            <div className="hdr-title">MENDES <span>✂</span> CUT</div>
            <div className="hdr-sub">Barber Shop · Meyrin</div>
          </div>
          <span style={{ color: GOLD, fontSize: 22, opacity: 0.7 }}>✂</span>
        </div>

        <div className="nav">
          {[{ id: "book", icon: "📅", label: "Réserver" }, { id: "social", icon: "◈", label: "Réseaux" }, { id: "lieu", icon: "◉", label: "Lieu" }].map(t => (
            <button key={t.id} className={`nb ${tab === t.id ? "on" : ""}`} onClick={() => setTab(t.id)}>
              <span style={{ fontSize: 15 }}>{t.icon}</span>{t.label}
            </button>
          ))}
        </div>

        {tab === "book" && (
          <div className="pg">
            <div className="steps">{[1, 2, 3, 4].map(s => <div key={s} className="step-bar" style={{ background: step >= s ? GOLD : BORDER }} />)}</div>

            {step === 1 && !booked && (
              <>
                <div className="sh">Choisir un <span>service</span></div>
                <div className="ss">Sélectionnez votre prestation</div>
                <div className="svc-grid">
                  {SERVICES.map(s => (
                    <div key={s.id} className={`svc ${svcId === s.id ? "sel" : ""} ${s.featured ? "feat" : ""}`} onClick={() => setSvcId(s.id)}>
                      {s.featured && <div className="featured-badge">Signature</div>}
                      <div className="svc-cat">{s.category}</div>
                      <div className="svc-name">{s.name}</div>
                      <div className="svc-desc">{s.desc}</div>
                      <div className="svc-foot"><span className="svc-price">{s.price} CHF</span><span className="svc-dur">{s.duration}</span></div>
                    </div>
                  ))}
                </div>
                <div className="supp-title">✦ Suppléments (optionnel)</div>
                <div className="supp-list">
                  {SUPPLEMENTS.map(s => (
                    <div key={s.id} className={`supp-item ${suppIds.includes(s.id) ? "sel" : ""}`} onClick={() => toggleSupp(s.id)}>
                      <div className="supp-check">{suppIds.includes(s.id) ? "✓" : ""}</div>
                      <span className="supp-name">{s.name}</span>
                      <span className="supp-price">+{s.price} CHF</span>
                    </div>
                  ))}
                </div>
                <button className="btn" disabled={!svcId} onClick={() => setStep(2)}>Continuer →</button>
              </>
            )}

            {step === 2 && !booked && (
              <>
                <div className="sh">Date & <span>Heure</span></div>
                <div className="ss">Choisissez votre créneau</div>

                {/* ✅ Indicateur de chargement */}
                {loadingSlots && <div className="refresh-indicator">↻ Mise à jour des disponibilités...</div>}

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <button onClick={prevMonth} style={{ background: "none", border: `1px solid ${BORDER}`, borderRadius: 3, color: MUTED, width: 32, height: 32, cursor: "pointer", fontSize: 16 }}>‹</button>
                  <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 18, letterSpacing: "0.08em" }}>{MONTHS_FR[calMonth]} <span style={{ color: GOLD }}>{calYear}</span></div>
                  <button onClick={nextMonth} style={{ background: "none", border: `1px solid ${BORDER}`, borderRadius: 3, color: MUTED, width: 32, height: 32, cursor: "pointer", fontSize: 16 }}>›</button>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 4, marginBottom: 16 }}>
                  {["L","M","M","J","V","S"].map((d, i) => <div key={i} style={{ textAlign: "center", fontSize: 9, color: MUTED, paddingBottom: 6 }}>{d}</div>)}
                  {(() => { if (!days[0]) return null; const f = days[0].full.getDay(); const o = f === 0 ? 6 : f - 1; return Array(o).fill(null).map((_, i) => <div key={`e${i}`} />); })()}
                  {days.map((d, i) => {
                    const dateISO = toISODate(d.full.getFullYear(), d.month, d.date);
                    const dayBlocked = isDayBlocked(dateISO);
                    const isSel = dayIdx === i;
                    return (
                      <div key={i} onClick={() => { if (dayBlocked) return; setDayIdx(i); setSlot(null); }} style={{
                        background: isSel ? "rgba(200,169,110,.1)" : CARD,
                        border: `1px solid ${isSel ? GOLD : BORDER}`,
                        borderRadius: 3, padding: "8px 4px", cursor: dayBlocked ? "not-allowed" : "pointer",
                        textAlign: "center", opacity: dayBlocked ? 0.35 : 1, minWidth: 0,
                      }}>
                        <div style={{ fontSize: 7, color: isSel ? GOLD : MUTED, textTransform: "uppercase" }}>{DAYS_SHORT[d.dayIdx]}</div>
                        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 16, color: isSel ? GOLD : "var(--text)" }}>{d.date}</div>
                        {dayBlocked && <div className="day-closed">Fermé</div>}
                      </div>
                    );
                  })}
                </div>

                {dayIdx !== null && (
                  <div className="slot-legend">
                    <div className="legend-item"><div className="legend-dot" style={{ background: CARD, border: `1px solid ${BORDER}` }} />Libre</div>
                    <div className="legend-item"><div className="legend-dot" style={{ background: "rgba(200,80,80,.1)", border: "1px solid rgba(200,80,80,.3)" }} />Réservé</div>
                    <div className="legend-item"><div className="legend-dot" style={{ background: "rgba(100,100,100,.15)", border: "1px solid rgba(100,100,100,.2)" }} />Indisponible</div>
                  </div>
                )}

                {dayIdx !== null && (() => {
                  const d = days[dayIdx];
                  const dateISO = toISODate(d.full.getFullYear(), d.month, d.date);
                  const renderSlots = (slots) => slots.map(s => {
                    const past = isSlotPast(dateISO, s);
                    const blocked = !past && isSlotBlocked(dateISO, s);
                    const taken = !past && !blocked && isSlotTaken(dateISO, s);
                    return (
                      <div key={s} className={`slot ${past || blocked ? "off" : ""} ${taken ? "taken" : ""} ${slot === s ? "sel" : ""}`}
                        onClick={() => !past && !blocked && !taken && setSlot(s)}>{s}</div>
                    );
                  });
                  return (
                    <>
                      <div className="slot-period">Matin & Midi</div>
                      <div className="slot-grid">{renderSlots(SLOTS_AM)}</div>
                      <div className="slot-period">Après-midi</div>
                      <div className="slot-grid">{renderSlots(SLOTS_PM)}</div>
                    </>
                  );
                })()}

                {/* ✅ Message si créneau pris entre temps */}
                {saveError && step === 2 && (
                  <div style={{ background: "rgba(200,80,80,0.1)", border: "1px solid rgba(200,80,80,0.3)", borderRadius: 3, padding: "10px 12px", fontSize: 11, color: "#d08080", marginBottom: 12 }}>
                    ⚠ Ce créneau vient d'être pris. Merci d'en choisir un autre.
                  </div>
                )}

                <button className="btn" disabled={!slot || dayIdx === null} onClick={() => setStep(3)}>Continuer →</button>
                <button className="btn-ghost" onClick={() => setStep(1)}>← Retour</button>
              </>
            )}

            {step === 3 && !booked && (
              <>
                <div className="sh">Vos <span>infos</span></div>
                <div className="ss">Pour la confirmation SMS</div>
                <div className="sum">
                  <div className="sum-row"><span className="sum-label">Service</span><span className="sum-val gold">{svc?.name}</span></div>
                  {supps.length > 0 && <div className="sum-row"><span className="sum-label">Suppléments</span><span className="sum-val" style={{ fontSize: 10, color: MUTED }}>{supps.map(s => s.name).join(", ")}</span></div>}
                  <div className="sum-row"><span className="sum-label">Date</span><span className="sum-val">{dayIdx !== null ? `${DAYS_FULL[days[dayIdx].dayIdx]} ${days[dayIdx].date}` : ""}</span></div>
                  <div className="sum-row"><span className="sum-label">Heure</span><span className="sum-val">{slot}</span></div>
                  <div className="sum-row"><span className="sum-label">Total</span><span className="sum-val gold sum-total">{total} CHF</span></div>
                </div>
                <div className="field"><label>Prénom & Nom</label><input type="text" placeholder="Jean Dupont" value={name} onChange={e => setName(e.target.value)} /></div>
                <div className="field">
                  <label>Numéro de téléphone</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <select value={countryCode} onChange={e => setCountryCode(e.target.value)} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 3, color: "var(--text)", fontFamily: "'Lato',sans-serif", fontSize: 13, padding: "12px 6px", outline: "none", flexShrink: 0, width: 110 }}>
                      {[
                        { code: "+41", label: "🇨🇭 +41" }, { code: "+33", label: "🇫🇷 +33" },
                        { code: "+32", label: "🇧🇪 +32" }, { code: "+49", label: "🇩🇪 +49" },
                        { code: "+39", label: "🇮🇹 +39" }, { code: "+34", label: "🇪🇸 +34" },
                        { code: "+351", label: "🇵🇹 +351" }, { code: "+44", label: "🇬🇧 +44" },
                        { code: "+1", label: "🇺🇸 +1" }, { code: "+55", label: "🇧🇷 +55" },
                        { code: "+212", label: "🇲🇦 +212" }, { code: "+213", label: "🇩🇿 +213" },
                        { code: "+216", label: "🇹🇳 +216" }, { code: "+221", label: "🇸🇳 +221" },
                        { code: "+225", label: "🇨🇮 +225" }, { code: "+237", label: "🇨🇲 +237" },
                        { code: "+243", label: "🇨🇩 +243" }, { code: "+244", label: "🇦🇴 +244" },
                        { code: "+245", label: "🇬🇼 +245" }, { code: "+238", label: "🇨🇻 +238" },
                        { code: "+90", label: "🇹🇷 +90" }, { code: "+31", label: "🇳🇱 +31" },
                        { code: "+43", label: "🇦🇹 +43" }, { code: "+352", label: "🇱🇺 +352" },
                        { code: "+48", label: "🇵🇱 +48" }, { code: "+7", label: "🇷🇺 +7" },
                        { code: "+86", label: "🇨🇳 +86" }, { code: "+91", label: "🇮🇳 +91" },
                      ].map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                    <input type="tel"
                      placeholder={{ "+41": "79 172 35 75", "+33": "6 12 34 56 78", "+32": "470 12 34 56", "+49": "151 234 56789", "+39": "312 345 6789", "+34": "612 345 678", "+351": "912 345 678", "+44": "7911 123456", "+1": "202 555 0123", "+55": "11 91234 5678", "+212": "6 12 34 56 78", "+213": "551 23 45 67", "+216": "20 123 456", "+221": "77 123 45 67", "+225": "07 12 34 56 78", "+237": "6 71 23 45 67", "+243": "81 234 5678", "+244": "923 123 456", "+245": "955 123 456", "+238": "991 23 45", "+90": "532 123 45 67", "+31": "6 12345678", "+43": "664 123456", "+352": "621 123 456", "+48": "512 345 678", "+7": "912 345-67-89", "+86": "131 2345 6789", "+91": "98765 43210" }[countryCode] || "Numéro"}
                      value={phone} onChange={e => setPhone(e.target.value)}
                      style={{ flex: 1, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 3, padding: "12px 13px", color: "var(--text)", fontFamily: "'Lato',sans-serif", fontSize: 16, outline: "none" }}
                    />
                  </div>
                </div>
                <div className="notice">
                  <span className="notice-icon">💬</span>
                  <div className="notice-text"><strong>Confirmation par SMS</strong>Vous recevrez un SMS + un rappel 24h avant votre rendez-vous.</div>
                </div>
                <button className="btn" disabled={name.trim().length < 2 || phone.trim().length < 6 || saving} onClick={saveReservation}>
                  {saving ? "Vérification..." : "Confirmer le RDV ✓"}
                </button>
                {saveError && step === 3 && <div style={{ marginTop: 10, fontSize: 11, color: "#e07070", textAlign: "center", wordBreak: "break-word" }}>{saveErrorMsg || "Erreur de connexion — réessaie."}</div>}
                <button className="btn-ghost" onClick={() => setStep(2)}>← Retour</button>
              </>
            )}

            {step === 4 && booked && (
              <>
                <div className="confirm-center">
                  <div className="confirm-ring">✓</div>
                  <div className="confirm-h">RDV <span>Confirmé</span></div>
                  <div className="confirm-s">Merci {name.split(" ")[0]} — à très bientôt !</div>
                </div>
                <div className="sum">
                  <div className="sum-row"><span className="sum-label">Service</span><span className="sum-val gold">{svc?.name}</span></div>
                  {supps.length > 0 && supps.map(s => <div className="sum-row" key={s.id}><span className="sum-label">+ {s.name}</span><span className="sum-val">{s.price} CHF</span></div>)}
                  <div className="sum-row"><span className="sum-label">Date</span><span className="sum-val">{dayIdx !== null ? `${DAYS_FULL[days[dayIdx].dayIdx]} ${days[dayIdx].date}` : ""} à {slot}</span></div>
                  <div className="sum-row"><span className="sum-label">Durée</span><span className="sum-val">{svc?.duration}</span></div>
                  <div className="sum-row"><span className="sum-label">Total</span><span className="sum-val gold sum-total">{total} CHF</span></div>
                </div>
                <div className="notice">
                  <span className="notice-icon">📱</span>
                  <div className="notice-text"><strong>SMS envoyé au {countryCode} {phone}</strong>Rappel 24h avant. Paiement espèces ou Twint uniquement.</div>
                </div>
                <div className="policy">
                  <div className="policy-title">⚠ Politique d'annulation</div>
                  <div className="policy-text">Absence non signalée facturée à <strong>50%</strong>. Prévenez au minimum <strong>24h à l'avance</strong>.</div>
                </div>
                <button className="btn" onClick={reset}>Nouveau rendez-vous</button>
                <button className="btn-ghost" onClick={() => setTab("lieu")}>◉ Voir le lieu</button>
              </>
            )}
          </div>
        )}

        {tab === "social" && (
          <div className="pg">
            <div className="sh">Nos <span>réseaux</span></div>
            <div className="ss">Suivez Mendes Cut</div>
            <a href="https://www.instagram.com/mendescut.barbershop/" target="_blank" rel="noreferrer" className="social-card">
              <div className="social-icon" style={{ background: "rgba(225,48,108,.15)" }}>
                <svg viewBox="0 0 24 24" fill="#E1306C" width="22" height="22"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </div>
              <div className="social-info"><div className="social-name">Instagram</div><div className="social-handle">@mendescut.barbershop</div></div>
              <div className="social-stat">Suivre ›</div>
            </a>
            <a href="tel:0791723575" className="social-card">
              <div className="social-icon" style={{ background: "rgba(50,200,100,.12)" }}>
                <svg viewBox="0 0 24 24" fill="#4CAF50" width="22" height="22"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
              </div>
              <div className="social-info"><div className="social-name">Téléphone</div><div className="social-handle">079 172 35 75</div></div>
              <div className="social-stat">Appeler ›</div>
            </a>
            <div className="divider" />
            <div className="policy">
              <div className="policy-title">💳 Mode de paiement</div>
              <div className="pay-methods">
                <span className="pay-pill pay-ok">✓ Espèces</span>
                <span className="pay-pill pay-ok">✓ Twint</span>
                <span className="pay-pill pay-no">✗ Carte</span>
              </div>
            </div>
            <div className="divider" />
            <div className="slogan">OBTIENS LE STYLE QUE TU MÉRITES</div>
          </div>
        )}

        {tab === "lieu" && (
          <div className="pg">
            <div className="sh">Nous <span>trouver</span></div>
            <div className="ss">60 avenue de Vaudagne · Meyrin</div>
            <div className="map-box">
              <div className="map-grid" />
              <div className="map-road-h" style={{ top: "50%" }} />
              <div className="map-road-h" style={{ top: "30%" }} />
              <div className="map-road-h" style={{ top: "70%" }} />
              <div className="map-road-v" style={{ left: "45%" }} />
              <div className="map-road-v" style={{ left: "70%" }} />
              <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} viewBox="0 0 400 180">
                <text x="140" y="87" fill="rgba(200,169,110,.35)" fontSize="8" fontFamily="Lato">Av. de Vaudagne</text>
              </svg>
              <div className="map-pin"><div className="map-pin-dot" /><div className="map-label">Mendes Cut</div></div>
            </div>
            <div className="addr-card">
              <div className="addr-row"><span className="addr-icon">📍</span><div><div className="addr-label">Adresse</div><div className="addr-val">60 avenue de Vaudagne<br />1217 Meyrin, Genève</div></div></div>
              <div className="addr-row"><span className="addr-icon">📞</span><div><div className="addr-label">Téléphone</div><div className="addr-val"><a href="tel:0791723575">079 172 35 75</a></div></div></div>
              <div className="addr-row"><span className="addr-icon">🕐</span><div><div className="addr-label">Horaires</div><div className="addr-val">Lun – Sam : 09h00 – 18h00<br />Dimanche : Fermé</div></div></div>
              <div className="addr-row"><span className="addr-icon">🚍</span><div><div className="addr-label">Transports</div><div className="addr-val">Bus 57 – Meyrin-Mairie<br />Tram 14 – Vaudagne</div></div></div>
            </div>
            <button className="btn" onClick={() => window.open("https://maps.google.com/?q=60+avenue+de+Vaudagne+1217+Meyrin", "_blank")}>
              Ouvrir dans Google Maps ↗
            </button>
            <div className="divider" />
            <div className="slogan">OBTIENS LE STYLE QUE TU MÉRITES</div>
          </div>
        )}
      </div>
      {showBanner && <InstallBanner onDismiss={dismissBanner} />}
    </>
  );
}
